import { useId, useState, useRef } from 'react';
import { X, Camera, Loader, Save, AlertCircle, Info } from 'lucide-react';
import { auth } from '../firebaseClient';
import { getApiBase, getAuthHeaders, setAuthToken } from '../utils/apiClient';
import { formatMinutes, hasTime, toMinutes } from '../utils/recipeTime';
import { TimeSelect } from './TimeSelect';
import { MealTypeSelect } from './MealTypeSelect';

export function PhotoRecipeForm({ isOpen, onClose, onSave }) {
  const [step, setStep] = useState('camera');
  // No serving size read from the photo -> Servings defaults to 4; say so at the field (neutral
  // info, doesn't block saving). Kept out of `recipe` so it's never sent with the save.
  // `announce` (role="status") only if it appears while the review step is already showing.
  const servingsNoteId = useId();
  const [servingsNote, setServingsNote] = useState({ show: false, announce: false });
  const servingsFromPhoto = (value) => {
    if (typeof value === 'number') return value > 0 ? value : null;
    const match = String(value ?? '').match(/\d+/);
    return match ? Number(match[0]) || null : null;
  };
  const [capturedImage, setCapturedImage] = useState(null);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const summaryRef = useRef(null);
  const [recipe, setRecipe] = useState({
    name: '',
    servings: 4,
    mealType: '',
    prepTime: null,
    cookTime: null,
    totalTime: null,
    source: '',
    ingredients: [''],
    method: [''],
    photo: '',
  });

  if (!isOpen) return null;

  const focusSummary = () => {
    requestAnimationFrame(() => {
      summaryRef.current?.focus();
    });
  };

  const clearFieldError = (field) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const validateRecipe = () => {
    const nextErrors = {};
    const normalizedIngredients = [];
    const normalizedMethod = [];

    const name = String(recipe.name || '').trim();
    if (!name) nextErrors.name = 'Recipe name is required.';

    recipe.ingredients.forEach((ingredient) => {
      const value = String(ingredient || '').trim();
      if (value) normalizedIngredients.push(value);
    });

    recipe.method.forEach((step) => {
      const value = String(step || '').trim();
      if (value) normalizedMethod.push(value);
    });

    if (!normalizedIngredients.length) nextErrors.ingredients = 'Add at least one ingredient.';
    if (!normalizedMethod.length) nextErrors.method = 'Add at least one method step.';

    // Mandatory, as in ManualRecipeForm: meal-plan generation matches on mealType, so a blank
    // value makes the recipe unreachable by the planner.
    const mealType = String(recipe.mealType || '').trim().toLowerCase();
    if (!mealType) nextErrors.mealType = 'Choose which meal type/s this recipe belongs to';

    return {
      isValid: Object.keys(nextErrors).length === 0,
      nextErrors,
      normalizedRecipe: {
        ...recipe,
        name,
        mealType,
        servings: Number(recipe.servings) || 4,
        prepTime: recipe.prepTime,
        cookTime: recipe.cookTime,
        totalTime: recipe.totalTime,
        source: String(recipe.source || '').trim(),
        ingredients: normalizedIngredients,
        method: normalizedMethod,
      },
    };
  };

  const normalizeIncoming = (data, photo) => {
    const toArray = (val) => {
      if (Array.isArray(val)) return val;
      if (typeof val === 'string') return val.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
      return [];
    };

    const ingredients = toArray(data?.ingredients).map((ing) => {
      if (!ing) return '';
      if (typeof ing === 'string') return ing;
      if (typeof ing === 'object') {
        const qty = ing.quantity ?? ing.qty ?? ing.amount ?? '';
        const unit = ing.unit ?? ing.unitText ?? '';
        const name = ing.name || ing.item || ing.ingredient || '';
        const lhs = [qty, unit].filter(Boolean).join(' ');
        return [lhs, name].filter(Boolean).join(' ').trim();
      }
      return String(ing || '').trim();
    }).filter(Boolean);

    const method = toArray(data?.method || data?.instructions || data?.steps).map((s) => String(s || '').trim()).filter(Boolean);

    return {
      name: data?.name || data?.title || '',
      servings: servingsFromPhoto(data?.servings) ?? 4,
      // The OCR route already asks Gemini for this and returns it; it was being dropped here,
      // so photo imports saved with no meal type and never appeared in a generated plan.
      mealType: String(data?.mealType || '').toLowerCase(),
      prepTime: toMinutes(data?.prepTime ?? data?.preparationTime),
      cookTime: toMinutes(data?.cookTime),
      totalTime: toMinutes(data?.totalTime),
      source: data?.source || '',
      ingredients: ingredients.length ? ingredients : [''],
      method: method.length ? method : [''],
      photo: photo || '',
    };
  };

  const handleFileCapture = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Convert to base64 for preview
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64Image = reader.result;
      setCapturedImage(base64Image);
      setStep('processing');
      setError('');

      try {
        const formData = new FormData();
        formData.append('file', file);

        // Ensure OCR request carries auth like other protected upload endpoints.
        const baseHeaders = getAuthHeaders();
        if (!baseHeaders.Authorization && auth?.currentUser) {
          const freshToken = await auth.currentUser.getIdToken(true);
          setAuthToken(freshToken);
          baseHeaders.Authorization = `Bearer ${freshToken}`;
        }

        const resp = await fetch(`${getApiBase()}/import/ocr`, {
          method: 'POST',
          headers: baseHeaders,
          body: formData,
        });

        if (!resp.ok) {
          let detail = '';
          try {
            const maybeJson = await resp.json();
            detail = maybeJson?.error || maybeJson?.details || '';
          } catch (_) {
            detail = await resp.text();
          }
          throw new Error(detail || `OCR failed (${resp.status})`);
        }

        const payload = await resp.json();
        const data = payload.recipe || payload;
        const parsed = normalizeIncoming(data, base64Image);
        setRecipe(parsed);
        setFieldErrors({});
        setServingsNote({ show: servingsFromPhoto(data?.servings) == null, announce: step === 'editing' });
      } catch (err) {
        console.error('Error processing image:', err);
        setError(err?.message || 'Could not extract text from the photo. You can edit manually.');
        setRecipe((prev) => ({ ...prev, photo: base64Image }));
        // Servings stays at the default 4 here too.
        setServingsNote({ show: true, announce: step === 'editing' });
      }

      setStep('editing');
    };
    reader.readAsDataURL(file);
  };

  const handleRetakePhoto = () => {
    setCapturedImage(null);
    setStep('camera');
    setServingsNote({ show: false, announce: false });
    setError('');
    setRecipe({
      name: '',
      servings: 4,
      mealType: '',
      prepTime: null,
      cookTime: null,
      totalTime: null,
      source: '',
      ingredients: [''],
      method: [''],
      photo: '',
    });
  };

  const handleAddIngredient = () => {
    clearFieldError('ingredients');
    setRecipe({
      ...recipe,
      ingredients: [...recipe.ingredients, ''],
    });
  };

  const handleRemoveIngredient = (index) => {
    clearFieldError('ingredients');
    setRecipe({
      ...recipe,
      ingredients: recipe.ingredients.filter((_, i) => i !== index),
    });
  };

  const handleIngredientChange = (index, value) => {
    const newIngredients = [...recipe.ingredients];
    newIngredients[index] = value;
    setRecipe({ ...recipe, ingredients: newIngredients });
    clearFieldError('ingredients');
  };

  const handleAddStep = () => {
    clearFieldError('method');
    setRecipe({
      ...recipe,
      method: [...recipe.method, ''],
    });
  };

  const handleRemoveStep = (index) => {
    clearFieldError('method');
    setRecipe({
      ...recipe,
      method: recipe.method.filter((_, i) => i !== index),
    });
  };

  const handleStepChange = (index, value) => {
    const newMethod = [...recipe.method];
    newMethod[index] = value;
    setRecipe({ ...recipe, method: newMethod });
    clearFieldError('method');
  };

  const handleSave = () => {
    const validation = validateRecipe();
    if (!validation.isValid) {
      setFieldErrors(validation.nextErrors);
      focusSummary();
      return;
    }

    setFieldErrors({});
    onSave(validation.normalizedRecipe);
    handleClose();
  };

  // Setting either half supersedes an OCR'd single total (see ManualRecipeForm).
  const setTime = (field) => (minutes) => setRecipe((prev) => {
    const next = { ...prev, [field]: minutes };
    if (hasTime(next.prepTime) && hasTime(next.cookTime)) next.totalTime = null;
    return next;
  });

  const handleClose = () => {
    setCapturedImage(null);
    setStep('camera');
    setServingsNote({ show: false, announce: false });
    setFieldErrors({});
    setRecipe({
      name: '',
      servings: 4,
      mealType: '',
      prepTime: null,
      cookTime: null,
      totalTime: null,
      source: '',
      ingredients: [''],
      method: [''],
      photo: '',
    });
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/30 z-40"
        onClick={step === 'camera' ? handleClose : undefined}
      />
      
      {/* Drawer */}
      <div className="fixed bottom-0 left-0 right-0 bg-card text-foreground rounded-t-3xl z-50 max-h-[95vh] overflow-hidden">
        <div className="max-w-md md:max-w-2xl w-full mx-auto flex flex-col max-h-[95vh]">
          {/* Handle bar */}
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-10 h-1 bg-muted rounded-full" />
          </div>

          {/* Camera Step */}
          {step === 'camera' && (
            <div className="px-6 pb-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl">Take a Photo</h2>
                <button
                  onClick={handleClose}
                  className="w-8 h-8 rounded-full hover:bg-muted flex items-center justify-center transition-colors"
                >
                  <X className="w-5 h-5 text-muted-foreground" />
                </button>
              </div>

              <div className="text-center">
                <div className="mb-6">
                  <div className="w-32 h-32 mx-auto rounded-2xl bg-muted flex items-center justify-center mb-4">
                    <Camera className="w-16 h-16 text-primary" />
                  </div>
                  <p className="text-foreground mb-2">
                    Take a photo of your recipe
                  </p>
                  <p className="text-sm text-muted-foreground">
                    We'll extract the recipe details for you to review and edit
                  </p>
                </div>

                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleFileCapture}
                  className="hidden"
                  id="camera-input"
                />
                <label
                  htmlFor="camera-input"
                  className="inline-block px-8 py-3 bg-primary text-primary-foreground rounded-full hover:bg-primary-hover transition-colors cursor-pointer"
                >
                  Open Camera
                </label>
              </div>
            </div>
          )}

          {/* Processing Step */}
          {step === 'processing' && (
            <div className="px-6 pb-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl">Processing Photo</h2>
              </div>

              <div className="text-center py-12">
                <Loader className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
                <p className="text-foreground mb-2">
                  Extracting recipe details...
                </p>
                <p className="text-sm text-muted-foreground">
                  This may take a few seconds
                </p>
              </div>
            </div>
          )}

          {/* Editing Step */}
          {step === 'editing' && (
            <>
              <div className="px-6 pb-2 border-b border-[var(--border)]">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl">Review & Edit</h2>
                  <button
                    onClick={handleClose}
                    className="w-8 h-8 rounded-full hover:bg-muted flex items-center justify-center transition-colors"
                  >
                    <X className="w-5 h-5 text-muted-foreground" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-6 pb-6">
                <form className="space-y-6 pt-6" noValidate>
                  {Object.keys(fieldErrors).length > 0 && (
                    <div
                      ref={summaryRef}
                      tabIndex={-1}
                      role="alert"
                      aria-live="assertive"
                      className="rounded-2xl border border-[#B85A5A]/30 bg-[#FEF5F5] px-4 py-3 outline-none focus:ring-2 focus:ring-[#B85A5A]"
                    >
                      <div className="flex items-start gap-2 text-[#B85A5A]">
                        <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#B85A5A]" />
                        <div className="space-y-1 text-sm">
                          <p className="font-medium">Please fix the highlighted fields below.</p>
                          <ul className="list-disc pl-5">
                            {fieldErrors.name && <li>{fieldErrors.name}</li>}
                            {fieldErrors.ingredients && <li>{fieldErrors.ingredients}</li>}
                            {fieldErrors.method && <li>{fieldErrors.method}</li>}
                            {fieldErrors.mealType && <li>{fieldErrors.mealType}</li>}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {error && (
                    <div className="rounded-xl bg-destructive/10 text-destructive text-sm px-4 py-3">
                      {error}
                    </div>
                  )}

                  {/* Photo Preview */}
                  {capturedImage && (
                    <div className="mb-4">
                      <img
                        src={capturedImage}
                        alt="Captured recipe"
                        className="w-full h-48 object-cover rounded-xl"
                      />
                      <button
                        type="button"
                        onClick={handleRetakePhoto}
                        className="mt-2 text-sm text-primary hover:text-primary-hover"
                      >
                        Retake Photo
                      </button>
                    </div>
                  )}

                  {/* Recipe Name */}
                  <div>
                    <label htmlFor="recipeName" className="block text-foreground mb-2">
                      Recipe Name *
                    </label>
                    <input
                      type="text"
                      id="recipeName"
                      value={recipe.name}
                      onChange={(e) => {
                        setRecipe({ ...recipe, name: e.target.value });
                        clearFieldError('name');
                      }}
                      aria-invalid={!!fieldErrors.name}
                      aria-describedby={fieldErrors.name ? 'photo-recipe-name-error' : undefined}
                      className={`w-full px-4 py-3 bg-input rounded-xl border-0 focus:ring-2 focus:ring-primary outline-none text-foreground placeholder-muted-foreground ${fieldErrors.name ? 'ring-2 ring-[#B85A5A]' : ''}`}
                      required
                    />
                    {fieldErrors.name && (
                      <div id="photo-recipe-name-error" className="mt-2 flex items-center gap-1.5 text-sm text-[#B85A5A]">
                        <AlertCircle className="h-4 w-4 text-[#B85A5A]" />
                        <span>{fieldErrors.name}</span>
                      </div>
                    )}
                  </div>

                  {/* Servings */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="servings" className="block text-foreground mb-2 text-sm">
                        Servings
                      </label>
                      <input
                        type="number"
                        id="servings"
                        value={recipe.servings}
                        aria-describedby={servingsNote.show ? servingsNoteId : undefined}
                        onChange={(e) => {
                          setRecipe({ ...recipe, servings: parseInt(e.target.value) });
                          setServingsNote({ show: false, announce: false }); // they've checked it
                        }}
                        className="w-full px-3 py-3 bg-input rounded-xl border-0 focus:ring-2 focus:ring-primary outline-none text-foreground placeholder-muted-foreground"
                        min="1"
                      />
                    </div>
                  </div>
                  {servingsNote.show && (
                    <p
                      id={servingsNoteId}
                      role={servingsNote.announce ? 'status' : undefined}
                      className="-mt-1 flex items-start gap-2 text-sm text-foreground"
                    >
                      <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#5F6B3F]" aria-hidden="true" />
                      <span>We couldn’t find a serving size for this recipe, so we’ve set it to 4. Please check it so your shopping list quantities are right.</span>
                    </p>
                  )}

                  {/* Prep & Cook Time — structured dropdowns, so an OCR value that came back
                      as "1 hour 30 minutes" is shown split out for review rather than parsed
                      silently at save time. */}
                  <div className="grid gap-4 md:grid-cols-2">
                    <TimeSelect
                      label="Prep Time"
                      idPrefix="prepTime"
                      minutes={recipe.prepTime}
                      onChange={setTime('prepTime')}
                    />
                    <TimeSelect
                      label="Cook Time"
                      idPrefix="cookTime"
                      minutes={recipe.cookTime}
                      onChange={setTime('cookTime')}
                    />
                  </div>

                  {hasTime(recipe.totalTime) && (
                    <p className="-mt-2 text-sm text-muted-foreground">
                      Imported total time: {formatMinutes(recipe.totalTime)}. Setting both prep and cook time will replace it.
                    </p>
                  )}

                  {!hasTime(recipe.totalTime) && (!hasTime(recipe.prepTime) || !hasTime(recipe.cookTime)) && (
                    <p className="-mt-2 text-sm text-muted-foreground">
                      Add prep + cook time to make this recipe filterable by time
                    </p>
                  )}

                  <MealTypeSelect
                    value={recipe.mealType}
                    onChange={(key) => {
                      setRecipe((prev) => ({ ...prev, mealType: key }));
                      clearFieldError('mealType');
                    }}
                    error={fieldErrors.mealType}
                    idPrefix="photoMealType"
                  />

                  {/* Source */}
                  <div>
                    <label htmlFor="source" className="block text-foreground mb-2">
                      Source
                    </label>
                    <input
                      type="text"
                      id="source"
                      value={recipe.source}
                      onChange={(e) => setRecipe({ ...recipe, source: e.target.value })}
                      className="w-full px-4 py-3 bg-input rounded-xl border-0 focus:ring-2 focus:ring-primary outline-none text-foreground placeholder-muted-foreground"
                    />
                  </div>

                  {/* Ingredients */}
                  <div>
                    <label className="block text-foreground mb-2">
                      Ingredients *
                    </label>
                    {fieldErrors.ingredients && (
                      <div className="mb-2 flex items-center gap-1.5 text-sm text-[#B85A5A]">
                        <AlertCircle className="h-4 w-4 text-[#B85A5A]" />
                        <span>{fieldErrors.ingredients}</span>
                      </div>
                    )}
                    <div className="space-y-2">
                      {recipe.ingredients.map((ingredient, index) => (
                        <div key={index} className="flex gap-2">
                          <input
                            type="text"
                            value={ingredient}
                            onChange={(e) => handleIngredientChange(index, e.target.value)}
                            placeholder={`Ingredient ${index + 1}`}
                            aria-invalid={!!fieldErrors.ingredients}
                            className={`flex-1 px-4 py-3 bg-input rounded-xl border-0 focus:ring-2 focus:ring-primary outline-none text-foreground placeholder-muted-foreground ${fieldErrors.ingredients ? 'ring-2 ring-[#B85A5A]' : ''}`}
                          />
                          {recipe.ingredients.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveIngredient(index)}
                              className="px-3 text-destructive hover:bg-muted rounded-xl transition-colors"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={handleAddIngredient}
                        className="text-sm text-primary hover:text-primary-hover"
                      >
                        + Add Ingredient
                      </button>
                    </div>
                  </div>

                  {/* Method */}
                  <div>
                    <label className="block text-foreground mb-2">
                      Method *
                    </label>
                    {fieldErrors.method && (
                      <div className="mb-2 flex items-center gap-1.5 text-sm text-[#B85A5A]">
                        <AlertCircle className="h-4 w-4 text-[#B85A5A]" />
                        <span>{fieldErrors.method}</span>
                      </div>
                    )}
                    <div className="space-y-2">
                      {recipe.method.map((step, index) => (
                        <div key={index} className="flex gap-2">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-muted flex items-center justify-center text-sm text-primary mt-3">
                            {index + 1}
                          </div>
                          <textarea
                            value={step}
                            onChange={(e) => handleStepChange(index, e.target.value)}
                            placeholder={`Step ${index + 1}`}
                            rows={2}
                            className="flex-1 px-4 py-3 bg-input rounded-xl border-0 focus:ring-2 focus:ring-primary outline-none resize-none text-foreground placeholder-muted-foreground"
                          />
                          {recipe.method.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveStep(index)}
                              className="px-3 text-destructive hover:bg-muted rounded-xl transition-colors"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={handleAddStep}
                        className="text-sm text-primary hover:text-primary-hover ml-8"
                      >
                        + Add Step
                      </button>
                    </div>
                  </div>
                </form>
              </div>

              {/* Bottom Actions */}
              <div className="px-6 py-4 border-t border-[var(--border)] bg-card">
                <div className="flex gap-3">
                  <button
                    onClick={handleClose}
                    className="flex-1 py-3 bg-card text-muted-foreground rounded-full border border-[var(--border)] hover:bg-muted transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="flex-1 py-3 bg-primary text-primary-foreground rounded-full hover:bg-primary-hover transition-colors"
                  >
                    <span className="inline-flex items-center justify-center gap-2">
                      <Save className="w-4 h-4" />
                      Save Recipe
                    </span>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
